import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, CalendarDays, Users, ClipboardCheck, Bell, LogOut, TreePine, Menu, X, Shield, CreditCard } from 'lucide-react';

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const ownerLinks = [
    { to: '/admin', icon: LayoutDashboard, label: 'Overview' },
    { to: '/admin/bookings', icon: CalendarDays, label: 'Bookings' },
    { to: '/admin/deposits', icon: CreditCard, label: 'Deposits' },
    { to: '/admin/users', icon: Users, label: 'Users' },
  ];

  const managerLinks = [
    { to: '/manager', icon: LayoutDashboard, label: 'Overview' },
    { to: '/manager/bookings', icon: CalendarDays, label: 'Bookings' },
  ];

  const cleanerLinks = [
    { to: '/cleaner', icon: ClipboardCheck, label: 'My Jobs' },
  ];

  const links = user?.role === 'owner' ? ownerLinks : user?.role === 'manager' ? managerLinks : cleanerLinks;

  return (
    <div className="flex h-screen bg-[#F9F8F6]" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className={`dashboard-sidebar fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:static md:flex md:flex-col`}>
        <div className="flex items-center gap-2 px-6 py-5 border-b border-white/10">
          <TreePine className="w-5 h-5 text-[#CC5A3D]" />
          <span className="font-heading text-lg font-medium">NF Caravans</span>
        </div>

        <div className="px-3 py-2">
          <p className="px-3 py-2 text-xs uppercase tracking-[0.2em] text-[#F9F8F6]/40 font-bold">{user?.role}</p>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {links.map(link => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded text-sm transition-colors ${
                location.pathname === link.to ? 'active bg-white/10 border-l-[3px] border-l-[#CC5A3D] text-white font-medium' : 'text-[#F9F8F6]/70 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`sidebar-${link.label.toLowerCase().replace(' ', '-')}`}
            >
              <link.icon className="w-4 h-4" />
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-white/10 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-3 py-2 text-sm text-[#F9F8F6]/70 hover:text-white transition-colors" onClick={() => setSidebarOpen(false)}>
            <TreePine className="w-4 h-4" />View Site
          </Link>
          <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2 text-sm text-[#F9F8F6]/70 hover:text-[#CC5A3D] transition-colors w-full" data-testid="sidebar-logout">
            <LogOut className="w-4 h-4" />Logout
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-[#E6D5B8] px-6 py-4 flex items-center justify-between">
          <button className="md:hidden" onClick={() => setSidebarOpen(true)} data-testid="dashboard-mobile-menu">
            <Menu className="w-5 h-5 text-[#1F2A25]" />
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#1E3F33]" />
            <span className="text-sm font-medium text-[#1F2A25]">{user?.name}</span>
            <span className="text-xs bg-[#F0EBE1] text-[#6B726A] px-2 py-0.5 rounded capitalize">{user?.role}</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/${user?.role === 'owner' ? 'admin' : user?.role}/notifications`} className="relative" data-testid="notifications-btn">
              <Bell className="w-5 h-5 text-[#6B726A] hover:text-[#1F2A25] transition-colors" />
            </Link>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
